import { useCallback, useMemo } from "react";
import { useBoardRolesFactory } from "./useBoardRoles";
import { RequiredTaskFields, useTaskRolesFactory } from "./useTaskRoles";

export interface UseTaskPermissionsFactoryOptions {
  entityId: string;
  meId: string | undefined;
}

export const DEFAULT_TASK_PERMISSIONS: TaskPermissions = {
  canEditTitle: false,
  canEditDescription: false,
  canEditBoard: false,
  canEditStatus: false,
  canEditDueDate: false,
  canEditAssignees: false,
  canEditLabel: false,
  canEditPriority: false,
  canEditCustomer: false,
  canEditTimeSpentOnTask: false,
  canLogTime: false,
  canEditRecurrence: false,
  canEditCustomFields: false,
  canManageCompleteInSpecificOrderOption: false,
  canCompleteTask: false,
  canSendUpdateToCustomer: false,
  canDuplicateTask: false,
  canArchiveTask: false,
  canRestoreTask: false,
  canDeleteTask: false,
  canManageChecklist: false,
  canEditChecklistItem: () => false,
  canEditOrDeleteComment: () => false,
  canManageAttachments: false,
  canManageOrder: false,
};

export const useTaskPermissionsFactory = ({
  entityId,
  meId,
}: UseTaskPermissionsFactoryOptions) => {
  const getTaskRoles = useTaskRolesFactory({ entityId, meId });
  const getBoardRoles = useBoardRolesFactory({ entityId, meId });

  const getTaskPermissions = useCallback(
    (task: RequiredTaskFields) => {
      const { isTaskCreator, isTaskAssignee } = getTaskRoles(task);
      const { isBoardAdmin, isBoardMember } = getBoardRoles(task.board_id);

      const canEditTaskInfo =
        isTaskCreator || isTaskAssignee || isBoardAdmin || isBoardMember;

      return {
        // Task info
        canEditTitle: canEditTaskInfo,
        canEditDescription: canEditTaskInfo,
        canEditBoard: canEditTaskInfo,
        canEditStatus: canEditTaskInfo, // but to other than "completed"
        canEditDueDate: canEditTaskInfo,
        canEditAssignees: canEditTaskInfo,
        canEditLabel: canEditTaskInfo,
        canEditPriority: canEditTaskInfo,
        canEditCustomer: canEditTaskInfo,
        canEditTimeSpentOnTask: canEditTaskInfo,
        canLogTime: canEditTaskInfo,
        canEditRecurrence: canEditTaskInfo,
        canEditCustomFields: canEditTaskInfo,
        canManageCompleteInSpecificOrderOption: canEditTaskInfo,

        // Task actions
        canCompleteTask: isTaskCreator || isTaskAssignee || isBoardAdmin,
        canSendUpdateToCustomer:
          isTaskCreator || isTaskAssignee || isBoardAdmin || isBoardMember,
        canDuplicateTask:
          isTaskCreator || isTaskAssignee || isBoardAdmin || isBoardMember,
        canArchiveTask: isTaskCreator || isTaskAssignee || isBoardAdmin,
        canRestoreTask: isTaskCreator || isTaskAssignee || isBoardAdmin,
        canDeleteTask: isTaskCreator || isTaskAssignee || isBoardAdmin,

        // Checklist
        /**
         * Manage checklist means the user can add, edit (also complete), reorder, or delete checklist items.
         * This flag is more generic than `canEditChecklistItem` flag and has always precedence over it.
         */
        canManageChecklist:
          isTaskCreator || isTaskAssignee || isBoardMember || isBoardAdmin,
        /**
         * Checks whether the user can edit (also complete) a specific checklist item
         */
        canEditChecklistItem: (itemAssigneeId: string) =>
          itemAssigneeId === meId,

        // Comments
        canEditOrDeleteComment: (commentCreatorId: string) =>
          commentCreatorId === meId,

        // Attachments
        canManageAttachments:
          isTaskCreator || isTaskAssignee || isBoardMember || isBoardAdmin,

        // Order
        canManageOrder:
          isTaskCreator || isTaskAssignee || isBoardMember || isBoardAdmin,
      };
    },
    [getBoardRoles, getTaskRoles, meId]
  );

  return getTaskPermissions;
};

export type TaskPermissions = ReturnType<
  ReturnType<typeof useTaskPermissionsFactory>
>;

interface UseTaskPermissionsOptions extends UseTaskPermissionsFactoryOptions {
  task: RequiredTaskFields | undefined;
}

export const useTaskPermissions = (options: UseTaskPermissionsOptions) => {
  const getTaskPermissions = useTaskPermissionsFactory(options);

  const taskPermissions = useMemo(
    () =>
      options.task
        ? getTaskPermissions(options.task)
        : DEFAULT_TASK_PERMISSIONS,
    [getTaskPermissions, options.task]
  );

  return taskPermissions;
};
